import {Component, OnInit} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {AppConfigService} from '../../../../Services/app-config.service';
import {NotificationService} from '../../../../Services/notification.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {NgbPaginationConfig} from '@ng-bootstrap/ng-bootstrap';
import {PagerStateService} from '../../../../Services/pager-state.service';
import {NgbpaginationConfigService} from '../../../../Services/ngbpagination-config.service';
import {Title} from '@angular/platform-browser';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {StripePaymentService} from '../../../../Services/stripe-payment.service';
import {StripeConnectApService} from '../../../../Services/stripe-connect-ap.service';
import {lastValueFrom} from 'rxjs';
import {ListPageBase} from '../../../list-page-base';

@Component({
  selector: 'app-stripe-admin-list',
  templateUrl: './stripe-admin-list.component.html',
  styleUrls: ['./stripe-admin-list.component.css'],
  standalone: false
})
export class StripeAdminListComponent extends ListPageBase implements OnInit {
  constructor(
    private aTable: DataService,
    private aToastr: NotificationService,
    private aRoute: Router,
    private ngbConfig: NgbPaginationConfig,
    private pagerConfig: NgbpaginationConfigService,
    private titleService: Title,
    private confirmDialogService: ConfirmDialogService,
    private stripePaymentService: StripePaymentService,
    private stripeApService: StripeConnectApService
  ) {
    super();
    this.ngbConfig = this.pagerConfig.getNgbPaginationConfig();
  }

  stripeAdminCount = 0;
  isLoading = false;

  pageSize = 20;

  vm: any = [];
  selectedEntityId = '';

  // Keep rows mostly as‑is; do minimal convenience mapping for display only.
  private normalizeRows(rows: any[]): any[] {
    return (rows || []).map((row: any) => ({
      ...row,
      // convenience aliases for display
      bankId: (row?.AppBankId ?? row?.appBankId ?? '').toString().trim(),
      bankName: (row?.AppBankName ?? row?.appBankName ?? '').toString().trim(),
      entityId: (row?.AppEntityId ?? row?.appEntityId ?? '').toString().trim(),
      entityName: (row?.EntityName ?? row?.entityName ?? '').toString().trim(),
      appBankId: (row?.AppBankId ?? row?.appBankId ?? '').toString().trim(),
      appEntityId: (row?.AppEntityId ?? row?.appEntityId ?? '').toString().trim(),
      flow: (row?.Flow ?? row?.flow ?? '').toString().trim().toUpperCase(),
      enabled: row?.Enabled !== undefined ? Boolean(row.Enabled) : Boolean(row?.enabled),
      isActive: row?.IsActive !== undefined ? Boolean(row.IsActive) : Boolean(row?.isActive),
      stripePaymentMethodId: (row?.StripePaymentMethodId ?? row?.stripePaymentMethodId ?? '').toString().trim(),
      // selection flag for UI
      isSelected: false,
    }));
  }

  ngOnInit() {
    this.restorePagerState();
    this.titleService.setTitle($localize`:Stripe Setup List@@stripeSetupList:Stripe Setup List`);
    this.vm.list = [];
    this.getData();
  }

  getData() {
    this.page = 1;
    this.startIndex = 1;
    this.endIndex = 0;
    this.getPagedApiData();
  }

  onNavigate(row: any) {
    const entityId = (row?.appEntityId || row?.AppEntityId || row?.entityId || row?.EntityId || '').toString().trim();
    const flow = (row?.flow || row?.Flow || '').toString().trim().toUpperCase();
    const queryParams = flow === 'AR' || flow === 'AP' ? {flow} : {};
    this.aRoute.navigate(['/admin/stripe-admin-form', entityId], {queryParams}).then();
  }

  onNew() {
    this.aRoute.navigate(['/admin/stripe-admin-form']).then();
  }

  onSelectRow(row: any, isSelected: boolean): void {
    const rowEntityId = (row?.appEntityId || row?.AppEntityId || row?.entityId || row?.EntityId || '').toString().trim();
    // Allow selecting a single row independently (no enforced pair selection and no exclusive single selection)
    // Toggle only the clicked row; keep other rows as-is.
    this.vm.list = (this.vm.list || []).map((item: any) => {
      const itemEntityId = (item?.appEntityId || item?.AppEntityId || item?.entityId || item?.EntityId || '').toString().trim();
      if (itemEntityId === rowEntityId && (item?.flow || item?.Flow) === (row?.flow || row?.Flow)) {
        return {
          ...item,
          isSelected,
        };
      }
      return item;
    });
    // Track last toggled entity for optional UX (not used to enforce selection)
    this.selectedEntityId = rowEntityId;
  }

  onDeleteSelected(): void {
    const selectedRow = (this.vm.list || []).find((row: any) => Boolean(row?.isSelected));
    if (!selectedRow) {
      this.aToastr.warning('Select an entity to delete.');
      return;
    }

    const appEntityId = (selectedRow?.appEntityId || selectedRow?.AppEntityId || selectedRow?.entityId || selectedRow?.EntityId || '').toString().trim();
    const entityName = (selectedRow?.entityName || selectedRow?.EntityName || appEntityId || 'this entity').toString().trim();
    const flow = (selectedRow?.flow || selectedRow?.Flow || '').toString().trim().toUpperCase();
    // New contract: explicit scope only. No fallback.
    // AR → legacy delete (flowType=1). AP handling under review; do not send type 2 here.
    if (flow !== 'AR' && flow !== 'AP') {
      this.aToastr.warning('Unable to determine flow (AR/AP) for deletion.');
      return;
    }
    if (!appEntityId) {
      this.aToastr.warning('Selected row is missing entity id.');
      return;
    }

    this.confirmDialogService.confirmThis(
      [
        `Are you sure you want to delete Stripe setup for ${entityName}?`,
        'Deleting from this page removes the entire profile and all setup for the selected entity.',
      ],
      () => {
        if (flow === 'AR') {
          // AR: legacy delete with explicit scope 1
          lastValueFrom(this.stripePaymentService.deleteStripeSetup(appEntityId, 1))
            .then(() => {
              this.aToastr.success('Deleted');
              this.selectedEntityId = '';
              this.getData();
            })
            .catch((error: HttpErrorResponse) => {
              const msg = error?.error?.Message || error.message || 'Error deleting Stripe setup (AR).';
              this.aToastr.error(msg);
            });
        } else {
          // AP: Route to rails Connect offboarding endpoint
          lastValueFrom(this.stripeApService.deleteConnect({appEntityId}))
            .then(() => {
              this.aToastr.success('AP Connect offboarding requested.');
              this.selectedEntityId = '';
              this.getData();
            })
            .catch((error: HttpErrorResponse) => {
              const msg = error?.error?.Message || error.message || 'Error offboarding AP (Connect).';
              this.aToastr.error(msg);
            });
        }
      },
      () => {
        this.aToastr.success('Delete Canceled');
      }
    );
  }

  protected fetchPage(): void {
    this.getPagedApiData();
  }

  getPagedApiData() {
    this.isLoading = true;
    this.aTable.getData('StripeSetup', `?pageNumber=${this.page - 1}&pageSize=${this.pageSize}`).subscribe({
      next: (result: ResponseModel) => {
        this.vm.list = this.normalizeRows(result.Data || []);
        if (this.selectedEntityId) {
          this.vm.list = this.vm.list.map((row: any) => ({
            ...row,
            isSelected: (row?.appEntityId || row?.AppEntityId || row?.entityId || row?.EntityId || '').toString().trim() === this.selectedEntityId,
          }));
        }
        this.totalRecords = Number(result.totalRecords || this.vm.list.length);
        this.paginate(false);
      },
      error: (error: HttpErrorResponse) => {
        this.aToastr.error(error.message);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  serverSearch(value) {
    this.isLoading = true;
    this.aTable.getDataWithParams('StripeSetup', `${value}`).subscribe((res: ResponseModel) => {
      this.vm.list = this.normalizeRows(res.Data || []);
      if (this.selectedEntityId) {
        this.vm.list = this.vm.list.map((row: any) => ({
          ...row,
          isSelected: (row?.appEntityId || row?.AppEntityId || row?.entityId || row?.EntityId || '').toString().trim() === this.selectedEntityId,
        }));
      }
      this.totalRecords = Number(res.totalRecords || this.vm.list.length);
      this.paginate(false);
      this.isLoading = false;
    });
  }

  clearServerSearch(value) {
    if (value) {
      this.getData();
    }
  }

  hideSearch() {
    this.vm.searchHidden = !this.vm.searchHidden;
  }
}
